import { useCallback, useMemo, useState } from 'react';

import {
  getUnmetIdRequirements,
  hasUncheckedIdRequirementWaiver,
} from 'helpers/candidate_application/form-page-helpers';
import useFormSubmission from 'hooks/use-form-submission';
import usePageVisibilityRedirect from 'hooks/use-page-visibility-redirect';
import useResetSubsequentSections from 'hooks/use-reset-subsequent-sections';
import useVisibleSubsections from 'hooks/use-visible-subsections';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { CustomToast } from 'components/Toast/CustomToast';

import { selectCandidateApplication } from 'api/candidate-application-slice';

import SectionIndexPage from '../../CandidateSection/SectionIndexPage';
import FormPageRenderer from '../../FormPages/FormPageRenderer';
import PreSubsectionFlowRenderer from '../../PreSubsectionFlows/PreSubsectionFlowRenderer';

const VerifyIdentity = ({
  candidate,
  brand,
  handlers,
  token,
  sectionKey,
  pageIndex,
  section,
  subsections,
  hasIndexPage,
  isSinglePage,
  fetched,
  check_values,
  other_uploads,
}) => {
  const { t: translate } = useTranslation();
  const { candidateFields } = useSelector(selectCandidateApplication);
  const { buildSystemValuesWithReset } = useResetSubsequentSections(sectionKey, check_values);

  const selfieId = useMemo(() => {
    return other_uploads?.find(upload => upload.metadata?.selfie)?.id;
  }, [other_uploads]);

  const isSeflieRejected = useMemo(() => {
    return check_values?.rejected_values?.files?.some(id => id === selfieId);
  }, [check_values?.rejected_values?.files, selfieId]);

  const rejectedSelfieFileId = useMemo(() => {
    return check_values?.rejected_values?.files?.find(id => id === selfieId);
  }, [other_uploads, check_values?.rejected_values?.files]);

  // isSeflieRejected reflects an admin decision — the candidate only ever finds out about it
  // on a fresh visit to this section, not through a live, in-session change. Computed once
  // at mount (not a reactive useMemo) so that replacing the selfie later in the same visit —
  // e.g. a mid-verification retake triggered from a completely different, later page — can't
  // retroactively shrink this and throw off the current page's numbering. A genuine new
  // rejection is picked up correctly on the next visit/reload, when this remounts fresh.
  const [mountSnapshotPreSubsectionFlows] = useState(() =>
    other_uploads?.some(upload => upload.metadata?.selfie && !isSeflieRejected)
      ? []
      : section?.preSubsectionFlows || [],
  );

  // The candidate completing the pre-subsection flow (e.g. confirming their first selfie)
  // WHILE actually on that page is a different event from the "retake triggered from some
  // later page" case the mount-freeze above guards against — PreSubsectionFlowRenderer calls
  // onPreSubsectionFlowComplete right before it navigates on to the first real subsection, and
  // that navigation target is computed assuming preSubsectionFlows is now empty. Without this,
  // the frozen snapshot above never catches up, so the navigate lands on the same page index
  // the flow was already occupying and it just re-renders the same screen instead of advancing.
  const [preSubsectionFlowsCompletedThisVisit, setPreSubsectionFlowsCompletedThisVisit] =
    useState(false);
  const preSubsectionFlows = preSubsectionFlowsCompletedThisVisit
    ? []
    : mountSnapshotPreSubsectionFlows;

  const [customError, setCustomError] = useState('');

  const visibleSubsections = useVisibleSubsections(subsections);
  const { saveFormValuesAndSubmit } = useFormSubmission();

  // Calculate total pages including index page
  const totalPages =
    (hasIndexPage ? 1 : 0) + preSubsectionFlows?.length + visibleSubsections?.length;
  const currentPage = parseInt(pageIndex) || 0;

  // Adjust page index if index page exists
  const adjustedPageIndex = hasIndexPage ? currentPage - 1 : currentPage;

  // Redirect if current page is out of bounds after visibility changes
  // This handles the case where a page becomes invisible after requirements are met
  usePageVisibilityRedirect({
    currentPage,
    totalPages,
    visibleSubsections,
    preSubsectionFlows,
    hasIndexPage,
    sectionKey,
    token,
  });

  const handleFormSubmit = useCallback(
    async (formData, context, temp_Save = false, rejectedValuesFix = false) => {
      try {
        const hasValidationIssues = context?.hasValidationIssues || false;
        const currentPagePrefixes = context?.currentPagePrefixes || null;

        setCustomError('');

        const subsectionsToCheck =
          currentPage === totalPages - 1 ? visibleSubsections : context?.subsections;

        const unmetRequirements = getUnmetIdRequirements(
          subsectionsToCheck,
          candidateFields?.idRequirementsMet,
        );

        let hasCustomError = false;
        if (
          hasUncheckedIdRequirementWaiver({
            unmetRequirements,
            candidateFields,
            watchValues: formData,
            currentPagePrefixes,
          })
        ) {
          setCustomError(translate('candidate.sections.identityRequirementsNotMet'));
          hasCustomError = true;
          return false;
        }

        let status = 'in-progress'; // Initially empty
        if (currentPage === totalPages - 1 && !hasValidationIssues && !hasCustomError) {
          status = 'completed';
        } else if (hasValidationIssues) {
          status = 'in-progress';
        }

        // Build system values with current section status and reset logic
        const systemValues = buildSystemValuesWithReset('verify_identity_status', status);

        // Save the data and update state
        const fixResult = await saveFormValuesAndSubmit(
          sectionKey,
          formData,
          token,
          systemValues,
          currentPagePrefixes,
          rejectedValuesFix,
        );

        if (currentPage === totalPages - 1 && !temp_Save) {
          toast.custom(
            t => (
              <CustomToast
                t={t}
                title={translate('candidate.sections.yourIdentityInformationIsSecureAndLocked')}
                message={translate('candidate.sections.yourIdentityInformationHasBeenSaved')}
                type="success"
              />
            ),
            {
              duration: 5000,
            },
          );
        }

        return fixResult ?? true;
      } catch (error) {
        toast.custom(
          t => (
            <CustomToast
              t={t}
              title={translate('candidate.sections.failedToSaveIdentityInformation')}
              message={error?.message || error || 'Please try again.'}
              type="error"
            />
          ),
          {
            duration: 5000,
          },
        );
        return false;
      }
    },
    [
      token,
      currentPage,
      totalPages,
      saveFormValuesAndSubmit,
      sectionKey,
      buildSystemValuesWithReset,
      candidateFields,
      visibleSubsections,
      translate,
    ],
  );

  // Page 0: Index page (if exists)
  if (hasIndexPage && currentPage === 0) {
    return <SectionIndexPage />;
  }

  // Pre-subsection flows
  if (adjustedPageIndex < preSubsectionFlows.length) {
    const flowComponentName = preSubsectionFlows[adjustedPageIndex];
    return (
      <PreSubsectionFlowRenderer
        componentName={flowComponentName}
        candidate={candidate}
        brand={brand}
        sectionKey={sectionKey}
        pageIndex={adjustedPageIndex}
        token={token}
        totalPages={totalPages}
        currentPage={currentPage}
        hasIndexPage={hasIndexPage}
        rejectedSelfieFileId={rejectedSelfieFileId}
        check_values={check_values}
        onPreSubsectionFlowComplete={() => setPreSubsectionFlowsCompletedThisVisit(true)}
      />
    );
  }

  // Regular subsections - FormPageRenderer handles both single and multi-page
  const subsectionIndex = adjustedPageIndex - preSubsectionFlows.length;
  if (subsectionIndex < visibleSubsections.length) {
    return (
      <FormPageRenderer
        subsections={visibleSubsections}
        candidate={candidate}
        brand={brand}
        handlers={handlers}
        fetched={fetched}
        singlePage={isSinglePage}
        sectionKey={sectionKey}
        pageIndex={isSinglePage ? undefined : subsectionIndex}
        globalPageIndex={currentPage}
        totalPages={totalPages}
        primaryActionLabel={translate('common.continue')}
        customError={customError}
        onFormSubmit={handleFormSubmit}
      />
    );
  }

  return <div>Page not found</div>;
};

export default VerifyIdentity;
